
import { Command, CommanderError, Option } from "commander";

import { CliError, EXIT_OK, EXIT_USAGE } from "./common.js";
import { formatFailure } from "./failures.js";
import { type RunState } from "./cli/registry.js";
import { registerToolCommands } from "./cli/register-tools.js";
import { registerReviewCommand } from "./cli/register-review.js";
import { registerScriptCommands } from "./cli/register-script.js";
import { registerWorkflowCommands } from "./cli/register-workflows.js";
import { loadLocalEnv } from "./infra/env.js";
import { PRODUCTION_LLM_COMPOSITION, type LlmComposition } from "./llm/composition.js";
import { LlmTransportError } from "./llm/types.js";
import { emitError } from "./output.js";
import { packageVersion } from "./package-root.js";



export function buildProgram(state: RunState, llm: LlmComposition = PRODUCTION_LLM_COMPOSITION): Command {
  const program = new Command("scriptctl")
    .description("Read, edit, ingest, and publish structured scripts")
    .usage("<command> [options]")
    .exitOverride()
    .allowExcessArguments(false)
    .allowUnknownOption(false)
    .showHelpAfterError(false)
    .configureOutput({
      writeOut: (text) => process.stdout.write(text),
      writeErr: () => {
        // Commander errors are emitted once through the scriptctl error model.
      },
    })
    .option("-j, --json", "Output structured JSON")
    .option("--format <format>", "Output format (addr)")
    .version(packageVersion(), "-v, --version", "Show the scriptctl version");

  registerWorkflowCommands(program, state, llm);
  registerScriptCommands(program, state);
  registerReviewCommand(program, state);
  registerToolCommands(program, state);
  return program;
}

function optionFor(command: Command, token: string): Option | undefined {
  const flag = token.split("=", 1)[0];
  let current: Command | null | undefined = command;
  while (current) {
    const option = current.options.find((candidate) => candidate.short === flag || candidate.long === flag);
    if (option) return option;
    current = current.parent;
  }
  return undefined;
}

interface ShortOptionScan {
  recognized: boolean;
  json: boolean;
  valueOption?: Option;
  hasAttachedValue: boolean;
}

function scanShortOptions(command: Command, token: string): ShortOptionScan {
  if (!token.startsWith("-") || token.startsWith("--") || token.length < 2) {
    return { recognized: false, json: false, hasAttachedValue: false };
  }
  let json = false;
  for (let index = 1; index < token.length; index += 1) {
    const option = optionFor(command, `-${token[index]}`);
    if (!option?.short) {
      return { recognized: false, json: false, hasAttachedValue: false };
    }
    if (option.short === "-j") json = true;
    if (option.required || option.optional) {
      return {
        recognized: true,
        json,
        valueOption: option,
        hasAttachedValue: index < token.length - 1,
      };
    }
  }
  return { recognized: true, json, hasAttachedValue: false };
}

function isRecognizedOptionToken(command: Command, token: string): boolean {
  if (token === "--") return true;
  if (token.startsWith("--")) return optionFor(command, token) !== undefined;
  return scanShortOptions(command, token).recognized;
}

function consumesNextValue(command: Command, option: Option, next: string | undefined): boolean {
  if (next === undefined || next === "--") return false;
  if (option.required) return !isRecognizedOptionToken(command, next);
  return option.optional && !next.startsWith("-");
}

function initialJsonMode(program: Command, args: readonly string[]): boolean {
  let command = program;
  for (let index = 0; index < args.length; index += 1) {
    const token = args[index]!;
    if (token === "--") break;
    if (token.startsWith("--")) {
      if (token === "--json") return true;
      const option = optionFor(command, token);
      if (
        option
        && !token.includes("=")
        && consumesNextValue(command, option, args[index + 1])
      ) index += 1;
      continue;
    }
    if (token.startsWith("-")) {
      const scanned = scanShortOptions(command, token);
      if (scanned.recognized && scanned.json) return true;
      if (
        scanned.recognized
        && scanned.valueOption
        && !scanned.hasAttachedValue
        && consumesNextValue(command, scanned.valueOption, args[index + 1])
      ) index += 1;
      continue;
    }
    const child = command.commands.find((candidate) => candidate.name() === token);
    if (child) command = child;
  }
  return false;
}

function helpTarget(program: Command, args: readonly string[]): string {
  let command = program;
  for (let index = 0; index < args.length; index += 1) {
    const token = args[index]!;
    if (token.startsWith("-")) {
      const option = optionFor(command, token);
      if (option && !token.includes("=") && (option.required || option.optional)) index += 1;
      continue;
    }
    const child = command.commands.find((candidate) => candidate.name() === token);
    if (!child) break;
    command = child;
  }
  const names: string[] = [];
  let current: Command | null | undefined = command;
  while (current && current.parent) {
    names.unshift(current.name());
    current = current.parent;
  }
  return ["scriptctl", ...names, "--help"].join(" ");
}

function commanderMessage(error: CommanderError): string {
  return error.message.replace(/^error:\s*/i, "").replace(/\.$/, "");
}

export async function main(
  argv: string[] | null = null,
  llm: LlmComposition = PRODUCTION_LLM_COMPOSITION,
): Promise<number> {
  loadLocalEnv();
  const args = argv ?? process.argv.slice(2);
  const state: RunState = { jsonMode: false, exitCode: EXIT_OK };
  const program = buildProgram(state, llm);
  state.jsonMode = initialJsonMode(program, args);

  if (args.length === 0) {
    program.outputHelp();
    return EXIT_OK;
  }

  try {
    await program.parseAsync(args, { from: "user" });
    return state.exitCode;
  } catch (error) {
    if (error instanceof CommanderError) {
      if (
        error.code === "commander.help"
        || error.code === "commander.helpDisplayed"
        || error.code === "commander.version"
      ) {
        return EXIT_OK;
      }
      return emitError(
        new CliError(commanderMessage(error), {
          exitCode: EXIT_USAGE,
          errorCode: "USAGE_ERROR",
          hint: `Run '${helpTarget(program, args)}'.`,
        }),
        state.jsonMode,
      );
    }
    if (error instanceof CliError) return emitError(error, state.jsonMode);
    if (error instanceof LlmTransportError) {
      return emitError(
        new CliError(formatFailure(error.failure), {
          exitCode: 70,
          errorCode: "RUNTIME_ERROR",
        }),
        state.jsonMode,
      );
    }
    const message = error instanceof Error ? error.message : String(error);
    return emitError(
      new CliError(message, {
        exitCode: 70,
        errorCode: "RUNTIME_ERROR",
      }),
      state.jsonMode,
    );
  }
}
